import { apiFetch } from '../lib/api';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { GeneratedImage, GeneratedImageUsage, ServerMessage } from '@roll1/shared';
import { useOptionalSessionContext } from './SessionContext';
import { useOptionalSheetContext } from './SheetContext';
import { useI18n } from '../i18n';
import type { TranslationKeys } from '../i18n';

/**
 * Generated pictures of the campaign (group Q, owner 25.10): the gallery list a member may see, live by `image:job` /
 * `image:deleted` (and read again on every (re)join — a restart turns running jobs into errors), my running jobs for
 * the gallery icon, the toasts «Готово» / «Не получилось» / «Поставить», the region edits waiting for «Принять»
 * (compare window), and the actions — start a portrait, repeat, delete. One provider for the table and Подготовка.
 */
export type StartResult = { ok: true; imageId: string } | { ok: false; error: string; code?: string; usedIn?: GeneratedImageUsage[] };

/** One action of the compare window (detail offers two: a new map, or the map of the overlapping link). */
export interface EditAction { label: string; run: (url: string) => void | Promise<void> }

/**
 * A region edit of a map (inpaint / outpaint / detail) started from a tool that closed at once (review item 2).
 * When its preview is ready: «Готово — сравнить» → the compare window (before / after) → an action or «Отклонить».
 */
export interface PendingEdit {
  imageId: string;
  /** What the toast and the window call it («Кисть: …»). */
  title: string;
  /** The picture before (the map now, or the crop of the region). */
  beforeUrl: string | null;
  actions: EditAction[];
}

export interface ImageGalleryValue {
  images: GeneratedImage[];
  /** My jobs that are generating now (the icon's state and number). */
  myActive: number;
  open: boolean;
  /** The picture the gallery shows in detail (null = the grid). */
  selectedId: string | null;
  openGallery: (selectId?: string | null) => void;
  closeGallery: () => void;
  toggleGallery: () => void;
  select: (id: string | null) => void;
  refresh: () => Promise<void>;
  /** `prompt`: a composed description instead of the sheet prompt («Составить описание с ИИ»). */
  startPortrait: (req: { sheetId: string; userPrompt?: string; style?: string; prompt?: string }) => Promise<StartResult>;
  /** «Составить описание с ИИ»: one English paragraph of what the character looks like, composed from the sheet. */
  describePortrait: (req: { sheetId: string; userPrompt?: string }) => Promise<{ ok: true; description: string } | { ok: false; error: string }>;
  repeat: (id: string) => Promise<StartResult>;
  remove: (id: string) => Promise<StartResult>;
  /** Put a finished map / portrait that was not put by itself (the place changed meanwhile). */
  place: (img: GeneratedImage) => void;
  registerEdit: (edit: PendingEdit) => void;
  /** The edit shown in the compare window (null = closed). */
  comparing: (PendingEdit & { image: GeneratedImage }) | null;
  closeCompare: () => void;
  acceptEdit: (actionIndex: number) => Promise<void>;
  rejectEdit: () => Promise<void>;
}

const Ctx = createContext<ImageGalleryValue | null>(null);

export function useImageGallery(): ImageGalleryValue | null {
  return useContext(Ctx);
}

const fill = (s: string, vars: Record<string, string | number>): string =>
  Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), s);

/** An unaccepted region-edit preview is not a gallery picture (review item 2). */
export const shownInGallery = (img: GeneratedImage): boolean => !img.preview || (img.usedIn?.length ?? 0) > 0;

export function ImageGalleryProvider({ children }: { children: ReactNode }) {
  const session = useOptionalSessionContext();
  const sheetsCtx = useOptionalSheetContext();
  const { t } = useI18n();
  const mp = session?.mp;
  const sessionId = mp?.session?.id ?? null;
  const me = mp?.participant?.id ?? null;
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const editsRef = useRef(new Map<string, PendingEdit>());
  const [comparingId, setComparingId] = useState<string | null>(null);

  // Latest names for the toasts (an event handler reads them after the state of the same tick has landed).
  const namesRef = useRef<{ pages: Array<{ id: string; name?: string }>; sheets: Array<{ id: string; name: string; portrait?: string | null }> }>({ pages: [], sheets: [] });
  namesRef.current = {
    pages: (session?.pages.pages ?? []) as Array<{ id: string; name?: string }>,
    sheets: (sheetsCtx?.sheets.sheets ?? []) as Array<{ id: string; name: string; portrait?: string | null }>,
  };

  const base = sessionId && me ? `/api/campaigns/${encodeURIComponent(sessionId)}/images` : null;
  const q = me ? `?participantId=${encodeURIComponent(me)}` : '';

  const refresh = useCallback(async (): Promise<void> => {
    if (!base) return;
    try {
      const r = await apiFetch(`${base}${q}`);
      if (!r.ok) return;
      const j = (await r.json()) as { images?: GeneratedImage[] };
      setImages(j.images ?? []);
    } catch { /* offline — the list stays */ }
  }, [base, q]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => { void refresh(); }, [refresh]);

  const post = useCallback(async (path: string, method: 'POST' | 'DELETE', body?: unknown): Promise<StartResult> => {
    if (!base) return { ok: false, error: t('images.offline') };
    try {
      const r = await apiFetch(`${base}${path}${q}`, {
        method,
        ...(body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
      });
      const j = (await r.json().catch(() => ({}))) as { imageId?: string; error?: string; code?: string; usedIn?: GeneratedImageUsage[] };
      if (!r.ok) return { ok: false, error: j.error ?? t('images.failedShort'), code: j.code, usedIn: j.usedIn };
      return { ok: true, imageId: j.imageId ?? '' };
    } catch {
      return { ok: false, error: t('images.offline') };
    }
  }, [base, q, t]);

  const describePortrait = useCallback(async (req: { sheetId: string; userPrompt?: string }): Promise<{ ok: true; description: string } | { ok: false; error: string }> => {
    if (!base) return { ok: false, error: t('images.offline') };
    try {
      const r = await apiFetch(`${base}/portrait/describe${q}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(req) });
      const j = (await r.json().catch(() => ({}))) as { description?: string; error?: string };
      if (!r.ok || typeof j.description !== 'string') return { ok: false, error: j.error ?? t('images.failedShort') };
      return { ok: true, description: j.description };
    } catch {
      return { ok: false, error: t('images.offline') };
    }
  }, [base, q, t]);

  const repeat = useCallback((id: string) => post(`/${encodeURIComponent(id)}/repeat`, 'POST'), [post]);
  const repeatRef = useRef(repeat);
  repeatRef.current = repeat;

  const openGallery = useCallback((selectId?: string | null) => {
    setOpen(true);
    if (selectId !== undefined) setSelectedId(selectId);
    void refresh();
  }, [refresh]);
  const openRef = useRef(openGallery);
  openRef.current = openGallery;

  const what = useCallback((img: GeneratedImage): string => {
    const kind = t(`images.what.${img.kind}` as keyof TranslationKeys);
    const name = img.target?.type === 'page'
      ? namesRef.current.pages.find((p) => p.id === img.target!.id)?.name
      : img.target?.type === 'sheet' ? namesRef.current.sheets.find((s) => s.id === img.target!.id)?.name : undefined;
    return name ? `${kind} «${name}»` : kind;
  }, [t]);

  /** «Поставить»: a map on its page (map:generation-accept — clears its drawings), a portrait on its sheet. */
  const place = useCallback((img: GeneratedImage): void => {
    if (!mp || !img.url || !img.target) return;
    if (img.kind === 'map' && img.target.type === 'page') {
      mp.send({ type: 'map:generation-accept', pageId: img.target.id, url: img.url, clearDrawings: true } as never);
    } else if (img.kind === 'portrait' && img.target.type === 'sheet') {
      mp.send({ type: 'sheet:set-portrait', sheetId: img.target.id, portrait: img.url } as never);
    }
    setTimeout(() => { void refreshRef.current(); }, 600);
  }, [mp]);
  const placeRef = useRef(place);
  placeRef.current = place;

  // Live: `image:job` (author + GMs), `image:deleted`, and a fresh list on every (re)join.
  useEffect(() => {
    if (!mp) return;
    return mp.onMessage((msg: ServerMessage) => {
      if (msg.type === 'session:joined' || msg.type === 'campaign:joined') {
        void refreshRef.current();
        return;
      }
      if (msg.type === 'image:deleted') {
        setImages((prev) => prev.filter((i) => i.id !== msg.imageId));
        return;
      }
      if (msg.type !== 'image:job') return;
      const img = msg.image;
      setImages((prev) => {
        const i = prev.findIndex((x) => x.id === img.id);
        if (i < 0) return [img, ...prev];
        const next = prev.slice();
        next[i] = { ...prev[i], ...img };
        return next;
      });
      if (img.authorMemberId !== me || img.status === 'generating') return;

      // A region edit of mine: «Готово — сравнить» (or one error toast — the tool itself has closed).
      const edit = editsRef.current.get(img.id);
      if (edit) {
        if (img.status === 'error') {
          editsRef.current.delete(img.id);
          toast.error(fill(t('images.toast.failed'), { what: edit.title }), { id: `image-${img.id}`, description: img.error ?? undefined });
          return;
        }
        toast.success(fill(t('images.toast.compare'), { what: edit.title }), {
          id: `image-${img.id}`,
          duration: Infinity,
          action: { label: t('images.compare'), onClick: () => setComparingId(img.id) },
        });
        return;
      }
      if (img.kind !== 'map' && img.kind !== 'portrait') return;
      if (img.status === 'error') {
        toast.error(fill(t('images.toast.failed'), { what: what(img) }), {
          id: `image-${img.id}`,
          description: img.error ?? undefined,
          action: img.canRepeat ? { label: t('images.retry'), onClick: () => { void repeatRef.current(img.id); } } : undefined,
        });
        return;
      }
      // Ready but not put (the page / the sheet changed meanwhile): «Поставить», with what it erases (review item 1).
      if (img.placed === false) {
        const drawings = img.offer?.drawings ?? 0;
        toast(fill(t('images.toast.notPlaced'), { what: what(img) }), {
          id: `image-${img.id}`,
          duration: Infinity,
          description: img.kind === 'map'
            ? (drawings > 0 ? fill(t('images.placeMapWarnDrawings'), { n: drawings }) : t('images.placeMapWarn'))
            : t('images.placePortraitWarn'),
          action: { label: t('images.setPortrait'), onClick: () => placeRef.current(img) },
        });
        return;
      }
      toast.success(fill(t('images.toast.ready'), { what: what(img) }), {
        id: `image-${img.id}`,
        action: { label: t('images.open'), onClick: () => openRef.current(img.id) },
      });
    });
  }, [mp, me, t, what]);

  const comparing = useMemo(() => {
    if (comparingId === null) return null;
    const edit = editsRef.current.get(comparingId);
    const image = images.find((i) => i.id === comparingId);
    return edit && image ? { ...edit, image } : null;
  }, [comparingId, images]);

  const remove = useCallback(async (id: string): Promise<StartResult> => {
    const r = await post(`/${encodeURIComponent(id)}`, 'DELETE');
    if (r.ok) {
      setImages((prev) => prev.filter((i) => i.id !== id));
      setSelectedId((cur) => (cur === id ? null : cur));
    }
    return r;
  }, [post]);

  const value = useMemo<ImageGalleryValue>(() => ({
    images,
    myActive: images.filter((i) => i.status === 'generating' && i.authorMemberId === me).length,
    open,
    selectedId,
    openGallery,
    closeGallery: () => setOpen(false),
    toggleGallery: () => (open ? setOpen(false) : openGallery()),
    select: setSelectedId,
    refresh,
    startPortrait: (req) => post('/portrait', 'POST', req),
    describePortrait,
    repeat,
    remove,
    place,
    registerEdit: (edit) => { editsRef.current.set(edit.imageId, edit); },
    comparing,
    closeCompare: () => setComparingId(null),
    acceptEdit: async (i) => {
      if (!comparing?.image.url) return;
      await comparing.actions[i]?.run(comparing.image.url);
      editsRef.current.delete(comparing.imageId);
      toast.dismiss(`image-${comparing.imageId}`);
      setComparingId(null);
      // Accepted = a gallery picture for good: it stays there when the map is replaced later.
      const id = comparing.imageId;
      setImages((prev) => prev.map((x) => (x.id === id ? { ...x, preview: false } : x)));
      await post(`/${encodeURIComponent(id)}/accept`, 'POST');
      setTimeout(() => { void refreshRef.current(); }, 600);
    },
    rejectEdit: async () => {
      if (!comparing) return;
      // Nothing was put; the preview goes with its file.
      await remove(comparing.imageId);
      editsRef.current.delete(comparing.imageId);
      toast.dismiss(`image-${comparing.imageId}`);
      setComparingId(null);
    },
  }), [images, me, open, selectedId, openGallery, refresh, post, describePortrait, repeat, remove, place, comparing]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
